import './css/main.css';

// Zero-JS baseline: the homepage renders and reveals entirely without
// JavaScript (success criterion 5). The old GSAP smoke tween was removed here
// because it set .fc-token-hero to opacity:0 up front, which would hide content
// behind JS. GSAP stays a package.json dependency for the Phase 4 animation
// entry (a separate, progressively-enhanced bundle), so nothing is uninstalled.

/*
 * Product flip card (PROD-04), progressive enhancement.
 *
 * Deliberately thin. The reveal is a native <dialog> opened with showModal(), so the
 * platform provides the focus trap, the inert background, Escape-to-close and the
 * focus return to the trigger. Hand-rolling any of that is where flip cards usually
 * break for keyboard and screen-reader users, so it is not hand-rolled here.
 *
 * Without JavaScript none of this runs, the CSS drops the detail back into flow under
 * the photo, and nothing is trapped. Guarded on the trigger existing, so it is inert
 * on every page that is not a category archive.
 */
( () => {
	const triggers = Array.from( document.querySelectorAll( '[data-fc-flip-open]' ) );
	if ( ! triggers.length ) {
		return;
	}

	const open = ( dialog ) => {
		if ( ! dialog ) {
			return;
		}
		// Older engines without showModal get the non-modal fallback rather than nothing.
		if ( typeof dialog.showModal === 'function' ) {
			dialog.showModal();
		} else {
			dialog.setAttribute( 'open', '' );
		}
	};

	triggers.forEach( ( trigger ) => {
		const dialog = document.getElementById( trigger.getAttribute( 'data-fc-flip-open' ) );
		if ( ! dialog ) {
			return;
		}

		trigger.addEventListener( 'click', () => open( dialog ) );

		dialog.querySelector( '[data-fc-flip-close]' )?.addEventListener( 'click', () => dialog.close() );

		// Clicking the backdrop closes it. The dialog element fills the whole viewport
		// while modal, so a click landing outside the card is a backdrop click.
		dialog.addEventListener( 'click', ( event ) => {
			if ( ! event.target.closest( '.fc-product-dialog__card' ) ) {
				dialog.close();
			}
		} );
	} );

	/*
	 * Deep link: /products/{slug}/ 301s to the category archive anchored at the card
	 * (inc/product-redirect.php), so a shared link opens straight onto its detail.
	 * Without JS it still lands on the right card, with the :target outline marking it.
	 */
	const openFromHash = () => {
		if ( ! window.location.hash ) {
			return;
		}

		let card = null;
		try {
			card = document.querySelector( window.location.hash );
		} catch ( e ) {
			return; // Malformed hash is not a selector; ignore.
		}

		if ( card && card.classList.contains( 'fc-product-tile--flip' ) ) {
			card.scrollIntoView( { block: 'nearest' } );
			open( card.querySelector( '.fc-product-dialog' ) );
		}
	};

	openFromHash();
	window.addEventListener( 'hashchange', openFromHash );
} )();

/*
 * Mobile navigation disclosure, progressive enhancement.
 *
 * With JavaScript disabled the primary nav renders as a plain visible stacked
 * list (CSS only collapses it behind the hamburger when html.fc-js is present),
 * so the menu is never trapped. This wires the button: toggling aria-expanded
 * and the .is-open class on the nav (which CSS reveals under the mobile
 * breakpoint). It closes on Escape (returning focus to the button), after a nav
 * link is chosen, and when the viewport grows back to desktop, so a stale open
 * state never persists. Guarded on the button + nav, so it is inert everywhere
 * the markup is absent.
 */
( () => {
	const toggle = document.querySelector( '.fc-nav-toggle' );
	const nav = document.getElementById( 'fc-primary-nav' );
	if ( ! toggle || ! nav ) {
		return;
	}

	const close = () => {
		nav.classList.remove( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'false' );
	};

	const open = () => {
		nav.classList.add( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'true' );
	};

	toggle.addEventListener( 'click', () => {
		if ( 'true' === toggle.getAttribute( 'aria-expanded' ) ) {
			close();
		} else {
			open();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key && nav.classList.contains( 'is-open' ) ) {
			close();
			toggle.focus();
		}
	} );

	nav.addEventListener( 'click', ( event ) => {
		if ( event.target.closest( 'a' ) ) {
			close();
		}
	} );

	// Reset to closed once the viewport reaches the desktop breakpoint, so the
	// collapsed mobile state never sticks after a rotate or resize.
	//
	// PAIRED VALUE: this must match the nav-collapse breakpoint in src/css/main.css
	// (the `max-width: 78.99rem` tier). Leaving it behind that value auto-closes a
	// panel that is still the active pattern at, say, 900px.
	//
	// History: 48rem originally; pushed out to 80rem when the links were enlarged;
	// pulled back to 79rem once the condensed divider treatment cut the header's
	// requirement to ~1210px, which widened the band where the full menu shows.
	const desktop = window.matchMedia( '(min-width: 79rem)' );
	desktop.addEventListener( 'change', ( event ) => {
		if ( event.matches ) {
			close();
		}
	} );
} )();

/*
 * Contact-page enquiry toggle (LEAD-02), progressive enhancement.
 *
 * Guarded on the presence of .fc-enquiry, so this is completely inert on every
 * other page (main.js is enqueued site-wide). With JavaScript disabled BOTH
 * forms render, each labelled, so no visitor is ever left without a form. When
 * this runs it marks the section enhanced (revealing the toggle via CSS), then
 * shows the data-default panel (detailed, the B2B-first cold default) and one
 * form at a time, switching on the tab control and moving focus to the shown
 * form's first field. The one-at-a-time state lives entirely here: CSS never
 * hides a panel by default, so turning JS off restores both panels.
 */
( () => {
	const enquiry = document.querySelector( '.fc-enquiry' );
	if ( ! enquiry ) {
		return;
	}

	const tabs = Array.from( enquiry.querySelectorAll( '.fc-enquiry__tab' ) );
	const panels = Array.from( enquiry.querySelectorAll( '.fc-enquiry__panel' ) );
	if ( ! tabs.length || ! panels.length ) {
		return;
	}

	const defaultForm = enquiry.getAttribute( 'data-default' ) || 'detailed';

	const showPanel = ( name, moveFocus ) => {
		panels.forEach( ( panel ) => {
			panel.hidden = panel.getAttribute( 'data-form' ) !== name;
		} );
		tabs.forEach( ( tab ) => {
			const selected = tab.getAttribute( 'data-target' ) === name;
			tab.setAttribute( 'aria-selected', selected ? 'true' : 'false' );
			tab.tabIndex = selected ? 0 : -1;
		} );
		if ( moveFocus ) {
			const active = enquiry.querySelector( `.fc-enquiry__panel[data-form="${ name }"]` );
			const firstField = active && active.querySelector( 'input:not([type="hidden"]), select, textarea' );
			if ( firstField ) {
				firstField.focus();
			}
		}
	};

	tabs.forEach( ( tab, index ) => {
		tab.addEventListener( 'click', () => showPanel( tab.getAttribute( 'data-target' ), true ) );
		// Left/right arrow keys move between the two tabs (ARIA tabs pattern).
		tab.addEventListener( 'keydown', ( event ) => {
			if ( event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' ) {
				return;
			}
			event.preventDefault();
			const next = event.key === 'ArrowRight' ? ( index + 1 ) % tabs.length : ( index - 1 + tabs.length ) % tabs.length;
			tabs[ next ].focus();
			showPanel( tabs[ next ].getAttribute( 'data-target' ), true );
		} );
	} );

	// Enhance last, so the toggle is only revealed once its behaviour is wired.
	enquiry.classList.add( 'is-enhanced' );
	showPanel( defaultForm, false );
} )();

/*
 * Testimonials carousel, progressive enhancement (vanilla, no GSAP so the zero-JS
 * baseline holds). Without JavaScript the quotes render as a horizontal scroll row
 * (every quote reachable). When this runs it switches the block to a 3D coverflow
 * stage: one quote centred and forward, its neighbours scaled back, rotated and
 * faded for depth. It auto-advances, exposes prev/next arrows, and PAUSES while a
 * pointer is over it or focus is inside it. Reduced-motion visitors get the arrows
 * but no auto-advance. Guarded on [data-fc-carousel], so it is inert elsewhere.
 */
( () => {
	const carousels = document.querySelectorAll( '[data-fc-carousel]' );
	if ( ! carousels.length ) {
		return;
	}
	const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	carousels.forEach( ( root ) => {
		const viewport = root.querySelector( '.fc-testimonials__viewport' );
		const track = root.querySelector( '.fc-testimonials__list' );
		const items = Array.from( root.querySelectorAll( '.fc-testimonials__item' ) );
		if ( ! viewport || ! track || items.length < 2 ) {
			return;
		}

		const prev = root.querySelector( '[data-fc-carousel-prev]' );
		const next = root.querySelector( '[data-fc-carousel-next]' );
		let index = 0;
		let timer = null;

		root.classList.add( 'is-carousel' );

		// Absolute cards positioned as a closed-loop rotary: the shortest wrapped
		// distance from the active index places every card on a side, so there is
		// always a neighbour left and right (a true infinite loop).
		const positionCards = () => {
			const n = items.length;
			items.forEach( ( el, idx ) => {
				let rel = ( ( ( idx - index ) % n ) + n ) % n;
				if ( rel > n / 2 ) {
					rel -= n;
				}
				const on = rel === 0;
				const abs = Math.abs( rel );
				el.classList.toggle( 'is-active', on );
				el.setAttribute( 'aria-hidden', on ? 'false' : 'true' );
				el.style.transform =
					`translate(-50%, -50%) translateX(${ rel * 64 }%) scale(${ on ? 1 : 0.8 }) rotateY(${ on ? 0 : rel < 0 ? 24 : -24 }deg)`;
				el.style.opacity = String( on ? 1 : abs === 1 ? 0.5 : 0.2 );
				el.style.zIndex = String( 100 - abs );
			} );
		};
		// Absolute cards leave the viewport with no height, so size it to the tallest.
		const sizeViewport = () => {
			let h = 0;
			items.forEach( ( el ) => {
				h = Math.max( h, el.scrollHeight );
			} );
			if ( h ) {
				viewport.style.height = `${ h + 16 }px`;
			}
		};
		const update = () => {
			sizeViewport();
			positionCards();
		};

		const go = ( n ) => {
			index = ( ( n % items.length ) + items.length ) % items.length;
			update();
		};
		const stop = () => {
			if ( timer ) {
				window.clearInterval( timer );
				timer = null;
			}
		};
		const start = () => {
			if ( reduce ) {
				return;
			}
			stop();
			timer = window.setInterval( () => go( index + 1 ), 4200 );
		};

		if ( prev ) {
			prev.addEventListener( 'click', () => {
				go( index - 1 );
				start();
			} );
		}
		if ( next ) {
			next.addEventListener( 'click', () => {
				go( index + 1 );
				start();
			} );
		}
		root.addEventListener( 'mouseenter', stop );
		root.addEventListener( 'mouseleave', start );
		root.addEventListener( 'focusin', stop );
		root.addEventListener( 'focusout', start );
		window.addEventListener( 'resize', update );

		update();
		// Re-measure once the web font has loaded and after full load: the fallback
		// font is shorter, so an early measure locks a too-small height and the real
		// Archivo text then overflows and clips (the quotes were being cut off).
		if ( document.fonts && document.fonts.ready ) {
			document.fonts.ready.then( update );
		}
		window.addEventListener( 'load', update );
		start();
	} );
} )();
